import {
  signOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  updateEmail,
  updatePassword,
  sendPasswordResetEmail,
  deleteUser,
} from "firebase/auth";

import { auth } from "../firebaseInit";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../store/firebaseInit";
import { isLocalhostRuntime } from "../appEnvironment";
import {
  bootstrapSession,
  deleteSiteMemberRecord,
  listManagedCardClubIds,
  loadSessionClaims,
} from "../../services/firebase/firestoreDataLayer";
import {
  completeLoadMeta,
  createIdleLoadMeta,
  failLoadMeta,
  startLoadMeta,
} from "../../services/firebase/loadState";

const localAdminEmails = [
  "mikeblomquist57@hotmail.com",
  "mikeblomquist57@outlook.com",
];
let authObserverStarted = false;
let authObserverReadyPromise = null;
let resolveAuthObserverReady = null;

const authModule = {
  namespaced: true,
  state() {
    return {
      user: "no user signed in",
      regErrorCode: null,
      regErrorMessage: null,
      userLoggedIn: false,
      showLoginLogOutDialog: false,
      showRegisterDialog: false,
      showAccountDialog: false,
      userIsAdmin: false,
      loginSuccess: false,
      loginMessage: "",
      passwordResetResult: "mlb-Password",
      registerSuccess: false,
      registerMessage: "mlb-register",
      authInitialized: false,
      loadMeta: createIdleLoadMeta(),
    };
  },

  getters: {
    //Getters for auth
    loginResult(state) {
      let result = {
        loggedIn: state.loginSuccess,
        message: state.loginMessage,
      };
      return result;
    },

    registerResult(state) {
      let result = {
        registered: state.registerSuccess,
        message: state.registerMessage,
      };
      return result;
    },

    passwordResetResult(state) {
      return state.passwordResetResult;
    },

    userDisplayName(state) {
      if (!state.user) {
        return "";
      }
      return state.user.displayName;
    },

    userEmailAddress(state) {
      if (!state.user) {
        return "";
      }
      return state.user.email;
    },

    userIsAdmin(state) {
      return state.userIsAdmin;
    },

    /* userIsSubsriber(state) {
      return state.userIsSubsriber;
    }, */

    loggedInSiteMemberID(state) {
      if (state.user) {
        return state.user.uid;
      }
      return null;
    },

    showLoginLogOutDialog(state) {
      return state.showLoginLogOutDialog;
    },

    showRegisterDialog(state) {
      return state.showRegisterDialog;
    },

    showAccountDialog(state) {
      return state.showAccountDialog;
    },

    userLoggedIn(state) {
      return state.userLoggedIn;
    },

    authInitialized(state) {
      return state.authInitialized;
    },

    authLoadState(state) {
      return state.loadMeta.loadState;
    },

    authLoadError(state) {
      return state.loadMeta.error;
    },
  },

  mutations: {
    writeUserLoggedIn(state, payLoad) {
      state.userLoggedIn = payLoad;
    },

    clearUserSession(state) {
      state.user = null;
      state.userLoggedIn = false;
      state.userIsAdmin = false;
      state.loginSuccess = false;
      state.loginMessage = "";
    },

    writeLoginResult(state, payLoad) {
      state.loginSuccess = payLoad.loggedIn;
      state.loginMessage = payLoad.message;
    },

    writeRegisterResult(state, payLoad) {
      state.registerSuccess = payLoad.registered;
      state.registerMessage = payLoad.message;
    },

    writePasswordResetResult(state, payLoad) {
      state.passwordResetResult = payLoad;
    },

    updateUserDisplayName(state, newName) {
      state.user.displayName = newName;
    },

    updateEmail(state, newEmail) {
      state.user.email = newEmail;
    },

    showLoginLogOutDialog(state) {
      state.showLoginLogOutDialog = true;
    },

    hideLoginLogOutDialog(state) {
      state.showLoginLogOutDialog = false;
    },

    showRegisterDialog(state) {
      state.showRegisterDialog = true;
    },

    hideRegisterDialog(state) {
      state.showRegisterDialog = false;
    },

    showAccountDialog(state) {
      state.showAccountDialog = true;
    },

    hideAccountDialog(state) {
      state.showAccountDialog = false;
    },

    authBootstrapStarted(state, requestKey) {
      state.loadMeta = startLoadMeta(requestKey);
    },

    authBootstrapSucceeded(state, result) {
      state.loadMeta = completeLoadMeta(state.loadMeta, result);
    },

    authBootstrapFailed(state, error) {
      state.loadMeta = failLoadMeta(state.loadMeta, error);
    },

    setUserIsAdmin(state, value) {
      state.userIsAdmin = value;
    },
  },

  /*  Database reads/writes are in actions.  */
  actions: {
    // This function is called when a user clicks the "Change Password" button on the account dialog
    async updatePassword({ commit }, payload) {
      // Update the password
      await updatePassword(auth.currentUser, payload.newPassword)
        .then(() => {
          // Show snackbar with success message
          commit("showSnackBar", payload.successText, { root: true });
        })
        .catch(() => {
          // Show snackbar with fail message
          commit("showSnackBar", payload.failText, { root: true });
        });
    },

    // This function is called when a user clicks the "Change Email" button on the account dialog
    async updateEmail({ commit }, payload) {
      let text = "";
      //
      await updateEmail(auth.currentUser, payload.newEmail)
        .then(() => {
          //Get success message from payload
          text = payload.successText;
          commit("updateEmail", payload.newEmail);
        })
        .catch(() => {
          // Get error message from payload
          text = payload.failText;
        });
      // Show snackbar with success or fail message
      commit("showSnackBar", text, { root: true });
    },

    // This function is called when a user clicks the "Change Display Name" button on the account dialog
    async updateProfile({ commit }, payload) {
      let text = "";
      await updateProfile(auth.currentUser, {
        displayName: payload.newDisplayName,
      })
        .then(() => {
          // Get success message from payload
          text = payload.successText;
        })
        .catch(() => {
          // Get error message from payload
          text = payload.failText;
        });
      // Show snackbar with success or fail message
      commit("showSnackBar", text, { root: true });
    },

    /* This function is called to register a new user */
    async registerUserWithEmail({ state, dispatch, commit }, creds) {
      let newUserCreated = false;
      let newUserSiteMemberPayload = null;

      //Create new user
      await createUserWithEmailAndPassword(auth, creds.email, creds.password)
        .then((userCredentials) => {
          state.user = userCredentials.user;

          newUserCreated = true;
          let payLoad = {
            registered: true,
            // This msg text is comming from the i18n file even though i18n is not directly imported in this file.
            message: "register.registerSuccess",
          };
          commit("writeRegisterResult", payLoad);
        })
        .catch((error) => {
          newUserCreated = false;
          state.regErrorCode = error.code;
          state.regErrorMessage = error.message;

          let msg = state.regErrorCode;
          if (state.regErrorCode.includes("use")) {
            // This msg text is comming from the i18n file even though i18n is not directly imported in this file.
            msg = "register.emailAlreadyRegistered";
          }
          let payLoad = {
            registered: false,
            message: msg,
          };
          commit("writeRegisterResult", payLoad);
        });

      if (!newUserCreated) {
        return {
          registered: state.registerSuccess,
          message: state.registerMessage,
        };
      }

      //If new user successfully created add new user screen name to profile
      if (newUserCreated) {
        await updateProfile(auth.currentUser, {
          displayName: creds.displayName,
        })
          .then(() => {
            commit("updateUserDisplayName", creds.displayName);
            newUserCreated = true;
          })
          .catch(() => {
            newUserCreated = false;
          });
      }
      /* if new user successfully created add default claims to user.  If user is mike or demouser make admin */
      if (newUserCreated) {
        if (isPrivilegedBootstrapUser(creds.email)) {
          let payload = {
            email: creds.email,
            admin: true,
            subscriber: creds.email !== "demouser@demo.com",
            subscriptionEndDate: buildSubscriptionEndDateClaim(50, 0),
          };

          if (creds.email === "demouser@demo.com") {
            payload.admin = false;
          }

          await dispatch("addProvidedClaims", payload)
            .then(() => {
              //special case for admin user. Set membership to 50 years and 0 days
              newUserSiteMemberPayload = {
                uid: state.user.uid,
                years: 50,
                days: 0,
              };

              newUserCreated = true;
            })
            .catch(() => {
              newUserCreated = false;
            });
        } else {
          await dispatch("addDefaultClaims", creds.email);
          //default case for non-admin user. Set membership to 0 years and -1 days
          newUserSiteMemberPayload = {
            uid: state.user.uid,
            years: 0,
            days: -1,
          };
        }
        //Log user out and then log back in so that updated credentials will
        //take effect
        await dispatch("writeDefaultMembership", newUserSiteMemberPayload);
        await dispatch("signOut");
        await dispatch("signinUserWithEmail", creds);
      }

      return {
        registered: state.registerSuccess,
        message: state.registerMessage,
      };
    },

    /* Add defualt claims to user.  Used to make user a default user with no admin rights */
    // eslint-disable-next-line
    async addDefaultClaims({}, email) {
      let payLoad = {
        email: email,
        admin: false,
        subscriber: false,
        subscriptionEndDate: null,
      };

      const addAdminRole = httpsCallable(functions, "updateClaims");
      await addAdminRole(payLoad);
    },

    /* Add/change user claims per provided input */
    // eslint-disable-next-line
    async addProvidedClaims({}, payLoad) {
      const addAdminRole = httpsCallable(functions, "updateClaims");
      await addAdminRole(payLoad);
    },

    /* Sign in user with provided email */
    async signinUserWithEmail({ state, commit }, creds) {
      await signInWithEmailAndPassword(auth, creds.email, creds.password)
        .then((userCredentials) => {
          state.user = userCredentials.user;

          let payLoad = {
            loggedIn: true,
            message: "logInOut.userLoggedIn",
          };
          commit("writeLoginResult", payLoad);
          commit("writeUserLoggedIn", true);
        })
        .catch((error) => {
          state.regErrorCode = error.code;
          state.regErrorMessage = error.message;

          let msg = "";
          if (state.regErrorCode.includes("user")) {
            // This msg text is comming from the i18n file even though i18n is not directly imported in this file.
            msg = "logInOut.emailNotFound";
          }
          if (state.regErrorCode.includes("password")) {
            // This msg text is comming from the i18n file even though i18n is not directly imported in this file.
            msg = "logInOut.passwordIncorrect";
          }

          let payLoad = {
            loggedIn: false,
            message: msg,
          };
          commit("writeLoginResult", payLoad);
        });
    },

    /* Send password reset email to the provide email */
    async sendPswdResetEmail({ commit }, email) {
      await sendPasswordResetEmail(auth, email)
        .then(() => {
          commit("writePasswordResetResult", "logInOut.passwordResetEmailSent");
        })
        .catch((error) => {
          commit("writePasswordResetResult", error.code);
        });
    },

    //Function to watch for changes in auth state
    authObserver({ state, commit }) {
      if (!authObserverReadyPromise) {
        authObserverReadyPromise = new Promise((resolve) => {
          resolveAuthObserverReady = resolve;
        });
      }

      if (authObserverStarted) {
        return authObserverReadyPromise;
      }

      authObserverStarted = true;
      commit("authBootstrapStarted", "auth-bootstrap");

      bootstrapSession().then((result) => {
        if (result.error) {
          commit("authBootstrapFailed", result.error);
          if (!state.authInitialized) {
            state.authInitialized = true;
            resolveAuthObserverReady?.();
            resolveAuthObserverReady = null;
          }
          return;
        }

        const user = result.data.user;
        state.user = user;
        state.userLoggedIn = !!user;
        state.userIsAdmin = false;
        state.userIsSubscriber = false;
        if (user && isLocalAdminFallbackUser(user.email)) {
          state.userIsAdmin = true;
        }

        commit("authBootstrapSucceeded", result);

        if (!state.authInitialized) {
          state.authInitialized = true;
          resolveAuthObserverReady?.();
          resolveAuthObserverReady = null;
        }

        if (user) {
          Promise.resolve(
            loadSessionClaims(user).then((claimsResult) => {
              if (claimsResult?.error) {
                return;
              }

              commit(
                "setUserIsAdmin",
                claimsResult.data.admin === true || isLocalAdminFallbackUser(user.email)
              );
            })
          ).catch(() => {});
        }
      });

      return authObserverReadyPromise;
    },

    async signOut({ commit }) {
      commit("clearUserSession");
      await signOut(auth).catch(() => {});
    },

    /* Function to delete user.  This function is called from the account dialog */
    async deleteUser({ state, dispatch }) {
      const clubListResult = await listManagedCardClubIds(state.user.uid);
      if (clubListResult.error) {
        throw clubListResult.error;
      }

      for (const clubId of clubListResult.data) {
        await dispatch("cc/deleteCardClub", clubId, { root: true });
      }

      const deleteSiteMemberResult = await deleteSiteMemberRecord(state.user.uid);
      if (deleteSiteMemberResult.error) {
        throw deleteSiteMemberResult.error;
      }

      /* Delete the Auth user */
      deleteUser(auth.currentUser)
        .then(() => {
          // User deleted.
          dispatch("signOut");
        })
        .catch(() => {});
    },

    /* Function to write the default site member info. This is called when a new user is created. And sets the membership expiration date to 1 day in the past. */
    async writeDefaultMembership({ state, dispatch }, payLoad) {
      let smPayLoad = {
        transaction: [
          {
            datePurchase: addToCurrentDate(0, 0),
            dateExpires: addToCurrentDate(payLoad.years, payLoad.days),
            userEmail: state.user.email,
            amountPaid: 0,
            ppOrder: { transaction: null },
          },
        ],
        userID: payLoad.uid,
      };

      await dispatch("sm/writeDefaultSiteMemberInfo", smPayLoad, {
        root: true,
      });
    },
  },
};

//Function to add years and days to current date
function addToCurrentDate(years, days) {
  let newDate = new Date();
  newDate.setFullYear(newDate.getFullYear() + years);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
}

function buildSubscriptionEndDateClaim(years, days) {
  return addToCurrentDate(years, days).toISOString();
}

function isPrivilegedBootstrapUser(email) {
  return localAdminEmails.includes(email) || email === "demouser@demo.com";
}

function isLocalAdminFallbackUser(email) {
  return isLocalhostRuntime() && localAdminEmails.includes(email);
}

export default authModule;
